/**
 * Debrief · the full article about one level, at `?debrief=<id>`.
 *
 * Why this is a screen and not a taller clear overlay. The overlay is the
 * moment a level ends: stars landing, XP counting, a rank that just moved. It
 * is read in about four seconds and then dismissed, and a thousand words
 * inside it would be scrolled past by every learner who has ever played a
 * game. The article is the other half of the same thought — the part a person
 * wants weeks later, when the same problem turns up at work — and that half
 * needs a URL, a table of contents, and a life longer than one animation.
 *
 * So: the reflection stays where it is and stays short (two paragraphs, "what
 * actually happened"), and this is where the level is actually explained. Both
 * are written by the author; neither is generated.
 *
 * The article is fetched one level at a time (web/src/lib/articles.ts). It is
 * never in `missions.json`: fifty of them is a third of a megabyte on the way
 * to the map, and a paid level's article is the paid level.
 *
 * ## Two languages
 *
 * The article is fetched in the reader's own, and a level nobody has
 * translated yet falls back to the English one rather than to a gate. So the
 * prose carries its own `dir` and `lang` rather than inheriting the
 * document's, and the reading estimate is counted at the pace of the language
 * that actually came back.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { DebriefProps } from '../contracts';
import type { PublicMission } from '../../types';
import { levelProgress, type Progress } from '../../progress';
import { paidSpan } from '../../lib/entitlement';
import { onward } from '../../lib/entitlement';
import { LOCALE_DIR, type Locale } from '../../lib/locale';
import { useLocale, useStrings } from '../../state/useLocale';
import { fetchArticle, type ArticleResult } from '../../lib/articles';
import { fetchOpenerIndex, openerForWorld, type OpenerCard } from '../../lib/openers';
import { md } from '../../lib/md';
import { useContents, type Heading } from '../../lib/contents';
import { Brand, Button, LinkButton, Machine, Tag } from '../../ui';
import { COURSE_HREF } from '../../lib/landing';

/**
 * Words a minute, by language. Low on purpose: this is technical prose with
 * commands in it.
 *
 * 190 was measured against the English articles and it does not transfer.
 * Arabic writes the same paragraph in about four fifths of the words — the
 * definite article is a prefix, pronouns and prepositions attach to the word
 * they belong to, and one form carries what English needs an auxiliary for —
 * so the same article, translated, is a shorter word count describing exactly
 * as much reading. Dividing it by the English figure would advertise a
 * translation as a fifth quicker than the piece it translates.
 *
 * 152 is 190 × 0.8, which is the number that makes an article and its
 * translation report the same minutes. It also lands inside the measured
 * range for Arabic silent reading, which is the check that it is not merely
 * an arithmetic convenience.
 *
 * German is the same argument pointing less far: it welds nouns into one word
 * that English writes as two or three, so a translation counts about a tenth
 * fewer words for the same article. 170 is 190 × 0.9, rounded.
 *
 * Japanese has no spaces to count words by, so it is counted in characters,
 * at 500 a minute (scripts/build-openers.mjs uses the same pair).
 */
const READING_PACE: Record<Locale, number> = { en: 190, ar: 152, de: 170, ja: 500 };

const readingUnits = (text: string, locale: Locale): number =>
  locale === 'ja' ? text.replace(/\s+/g, '').length : text.trim().split(/\s+/).length;

export function Debrief({ mission, missions, progress, cleared, account, onUnlock }: DebriefProps) {
  const [result, setResult] = useState<ArticleResult | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { locale } = useLocale();
  const strings = useStrings();
  const s = strings.reading;

  const article = result?.state === 'ready' ? result : null;
  const text = article?.text ?? null;

  /**
   * Fetch once the level is cleared, and not before. The Worker would answer
   * either way for a free level — it has no idea who has played what, and
   * docs/cheating.md is honest that it could not be told — so this is the
   * product's own line rather than a lock: a learner who has not finished is
   * offered the level, not the answer.
   */
  useEffect(() => {
    if (!cleared) {
      setResult(null);
      return;
    }
    let live = true;
    setResult(null);
    void fetchArticle(mission.id, locale).then((r) => {
      if (live) setResult(r);
    });
    return () => {
      live = false;
    };
  }, [mission.id, cleared, locale, account.user?.id]);

  // The contents rail, shared with the chapter openers (web/src/lib/contents.ts).
  // Keyed on the article text: that is the thing that changes when there is
  // something new to scan.
  const { headings, active } = useContents(body, text);

  /**
   * The language the article came back in, which is not always the one the
   * interface is in: an untranslated level answers in English rather than not
   * at all (lib/articles.ts).
   */
  const proseLocale: Locale = article?.locale ?? locale;
  const proseDir = LOCALE_DIR[proseLocale];

  // Counted at the pace of the language on the page, not the reader's: a
  // fallback to the English article is English prose and is read at 190.
  const minutes = useMemo(() => {
    if (!text) return null;
    return Math.max(1, Math.round(readingUnits(text, proseLocale) / READING_PACE[proseLocale]));
  }, [text, proseLocale]);

  /**
   * The chapter this level belongs to, when it has an opener written.
   *
   * Looked up through the index rather than by slugifying `mission.world`
   * here: the index carries the exact `world:` string as its join key, so a
   * world whose name does not survive a slugifier — an ampersand, a hyphen,
   * a word that changed last week — resolves or does not resolve for a reason
   * somebody can see, instead of silently producing a link to nothing.
   *
   * Null covers every way this can be absent: the index has not loaded, this
   * browser cannot reach it, or the world has no opener yet. All three render
   * no link, which is the right answer for all three.
   */
  const [opener, setOpener] = useState<OpenerCard | null>(null);
  useEffect(() => {
    let live = true;
    void fetchOpenerIndex(locale).then((index) => {
      if (live) setOpener(openerForWorld(index, mission.world));
    });
    return () => {
      live = false;
    };
  }, [mission.world, locale]);

  /**
   * The world's name, translated where it is drawn. The string itself is the
   * join key between the missions, the openers and the map, so it stays
   * English in the data and becomes a name here (docs/i18n.md §6.1).
   */
  const worldName = strings.home.worlds[mission.world]?.name ?? mission.world;

  /**
   * Where the foot of the article points.
   *
   * `missions.find((m) => m.level === mission.level + 1)` answered this, which
   * is the integer line rather than the path: it consulted neither the gate
   * nor the paywall, and once a second track ships it finds whichever lane
   * happens to sit first in the catalogue, because every track numbers itself
   * from zero (docs/curriculum.md §5). The shared helper reads `requires`
   * instead, and this takes the head of what it returns (App.tsx).
   *
   * `open` first and then `barred`, which lands on the same page the
   * arithmetic did and now says why. The foot of a debrief is a table of
   * contents: its job is to show that the course continues, so a level in the
   * paid half is still worth pointing at — the click lands back on the map
   * with the offer attached, which is a far better answer than a button
   * claiming every level is cleared. The workstation's Next button takes
   * `open` alone and stops there, because it makes a different promise:
   * press this and play.
   */
  const next = useMemo(() => {
    const path = onward(progress, missions, mission);
    return path.open[0] ?? path.barred[0] ?? null;
  }, [missions, mission, progress]);
  /** The nearest earlier level that has been cleared, so the reading has a "previous" too. */
  const prev = useMemo(() => prevRead(missions, mission, progress), [missions, mission, progress]);

  return (
    <div className="debrief">
      <header className="debrief-top">
        <Brand compact />
        <nav className="debrief-top-nav">
          <LinkButton variant="quiet" href={COURSE_HREF}>
            {s.levelList}
          </LinkButton>
          <LinkButton variant="quiet" href={`?mission=${encodeURIComponent(mission.id)}`}>
            {s.debrief.replay}
          </LinkButton>
        </nav>
      </header>

      <article className="debrief-page">
        <div className="debrief-head">
          <p className="debrief-eyebrow">
            <span className="debrief-level">{s.debrief.level(mission.level)}</span>
            {/* The world is a link once its chapter has an essay, and plain
                text until then — so a learner who wants the wider "why" can
                get to it from the level that raised the question. */}
            {opener ? (
              <a className="debrief-world is-link" href={`?opener=${encodeURIComponent(opener.slug)}`}>
                {worldName}
              </a>
            ) : (
              <span className="debrief-world">{worldName}</span>
            )}
          </p>
          <h1 className="debrief-title">{mission.title}</h1>
          <p className="debrief-meta">
            {/* A concept is a slug the machine joins on — `git.reflog` — so it
                is left in Latin and held to its own direction. */}
            <Tag>
              <Machine>{mission.concept}</Machine>
            </Tag>
            {minutes !== null && <span className="debrief-read">{s.readingTime(minutes)}</span>}
          </p>
        </div>

        <div className="debrief-body">
          <Contents headings={headings} active={active} />

          <div className="debrief-main">
            {!cleared ? (
              <Locked
                kind="unplayed"
                mission={mission}
                missions={missions}
                onUnlock={onUnlock}
                signedIn={account.user !== null}
              />
            ) : result === null ? (
              <p className="debrief-state" aria-live="polite">
                {s.debrief.opening}
              </p>
            ) : result.state === 'ready' ? (
              <motion.div
                ref={body}
                className="prose debrief-prose"
                dir={proseDir}
                lang={proseLocale}
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
                dangerouslySetInnerHTML={md(result.text)}
              />
            ) : (
              <Locked
                kind={result.state}
                mission={mission}
                missions={missions}
                onUnlock={onUnlock}
                signedIn={account.user !== null}
              />
            )}
          </div>
        </div>

        {result?.state === 'ready' && (
          <footer className="debrief-foot">
            {prev && (
              <LinkButton variant="quiet" href={`?debrief=${encodeURIComponent(prev.id)}`}>
                {/* The arrow is part of the sentence and it points the other
                    way in Arabic, so both directions are in the catalogue
                    rather than flipped here (lib/strings/reading.ts). */}
                {s.debrief.back(prev.level, prev.title)}
              </LinkButton>
            )}
            {next ? (
              <LinkButton variant="solid" href={`?mission=${encodeURIComponent(next.id)}`}>
                {next.track === 'checkpoint' ? next.title : s.debrief.next(next.level)}
              </LinkButton>
            ) : (
              <LinkButton variant="solid" href={COURSE_HREF}>
                {s.debrief.allCleared}
              </LinkButton>
            )}
          </footer>
        )}
      </article>
    </div>
  );
}

/**
 * The contents. Hidden below the width where it would sit on top of the text
 * rather than beside it (debrief.css) — not rendered as a collapsed thing the
 * reader has to open, because on a phone the scroll bar is the contents.
 *
 * The rail changes sides on its own: it is the first column of a grid, and a
 * grid's columns follow the document's direction.
 */
function Contents({ headings, active }: { headings: Heading[]; active: string | null }) {
  const s = useStrings().reading;
  if (headings.length < 2) return null;
  return (
    <nav className="debrief-toc" aria-label={s.contents}>
      <p className="label">{s.contents}</p>
      <ol>
        {headings.map((h) => (
          <li key={h.id} className={h.id === active ? 'is-here' : undefined}>
            {/* A real anchor, so it is linkable and the Back button works. The
                app's click delegate only claims hrefs starting with "?", so
                this is left alone and scrolls the way the browser does. */}
            <a href={`#${h.id}`}>{h.text}</a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Everything that is not an article: not played yet, not signed in, not
 * bought, not reachable. Each one says the true thing and offers the one
 * action that changes it — never a disabled button and never a shrug.
 */
function Locked({
  kind,
  mission,
  missions,
  onUnlock,
  signedIn,
}: {
  kind: 'unplayed' | 'missing' | 'anonymous' | 'unowned' | 'unavailable';
  mission: PublicMission;
  /**
   * The whole catalogue, for one sentence: the `unowned` gate says how many
   * levels are in the paid half and, when the catalogue can say it at all,
   * where that half starts. Both are facts about the course that move when a
   * chapter ships, and the second of them stops being a fact entirely once a
   * track is priced whole — which is why it is read here rather than written
   * into the string, and why the string has a version without it.
   */
  missions: PublicMission[];
  onUnlock(): void;
  signedIn: boolean;
}) {
  const r = useStrings().reading;
  const s = r.debrief;

  if (kind === 'unplayed') {
    return (
      <div className="debrief-gate">
        <h2>{s.unplayed.title}</h2>
        <p>{s.unplayed.body(mission.level)}</p>
        <LinkButton variant="solid" href={`?mission=${encodeURIComponent(mission.id)}`}>
          {s.unplayed.cta(mission.level)}
        </LinkButton>
      </div>
    );
  }

  if (kind === 'anonymous') {
    return (
      <div className="debrief-gate">
        <h2>{s.anonymous.title}</h2>
        <p>{s.anonymous.body}</p>
        <Button variant="solid" onClick={() => onUnlock()}>
          {s.getCourse}
        </Button>
      </div>
    );
  }

  if (kind === 'unowned') {
    const span = paidSpan(missions);
    // Null means nothing in this catalogue is locked, which cannot be true on
    // a page the server has just refused with a 402 — but the sentence should
    // not be a blank if it ever is, so the count falls back to this level and
    // everything after it *in its own lane*, which is what "from here on"
    // means anyway. The lane matters now that `level` is a position within a
    // track: counting forward across the whole catalogue would sweep up
    // another track's levels 20-51 because they share their numbers with
    // this one's (docs/curriculum.md §5).
    const count = span?.count ?? missions.filter((m) => m.track === mission.track && m.level >= mission.level).length;
    // The range is quoted only when it is this level's lane's range. "Every
    // level from 20 on" is a sentence about the rows around the one being
    // read, and a range belonging to another track names none of them — so
    // that reading counts instead, which is true of the paid half however it
    // is laid out (lib/entitlement.ts).
    const range = span?.range && span.range.track === mission.track ? span.range : null;
    return (
      <div className="debrief-gate">
        <h2>{s.unowned.title}</h2>
        <p>{range ? s.unowned.body(signedIn, range.from, count) : s.unowned.bodyCount(signedIn, count)}</p>
        <Button variant="solid" onClick={() => onUnlock()}>
          {s.getCourse}
        </Button>
      </div>
    );
  }

  if (kind === 'missing') {
    return (
      <div className="debrief-gate">
        <h2>{s.missing.title}</h2>
        <p>{s.missing.body}</p>
        <LinkButton variant="quiet" href={COURSE_HREF}>
          {r.levelList}
        </LinkButton>
      </div>
    );
  }

  return (
    <div className="debrief-gate">
      <h2>{s.unavailable.title}</h2>
      <p>{s.unavailable.body}</p>
      <Button variant="solid" onClick={() => window.location.reload()}>
        {r.tryAgain}
      </Button>
    </div>
  );
}

/**
 * The nearest earlier level this learner has cleared. Earlier debriefs they
 * have not earned are skipped rather than linked into a gate, so the back-link
 * always goes somewhere readable.
 *
 * "Nearest" used to mean the largest level number below this one, walked down
 * the integer line to zero with a `find` over the whole catalogue at every
 * step. That was correct while the course was a line and quadratic while it
 * was anything; it also had the same two faults the forward direction had —
 * a number is a position within a track now, so the walk would cross into
 * another lane's numbering, and a course that branches has no integer line to
 * walk down in the first place (docs/curriculum.md §5, §6).
 *
 * So it walks `requires` backwards instead, breadth first, and "nearest" now
 * means *fewest prerequisite hops away*, which is what the word was reaching
 * for all along: the reading immediately behind this one. On a single chain
 * that is level by level down, so the link lands exactly where it always did.
 * Where a level is built on two, the nearer pair is offered before either of
 * their prerequisites, and the tie between them goes to `requires` order —
 * which is the author's, and is also the order the map draws them in.
 *
 * `seen` is not there for the catalogue we have. `requires` is proved acyclic
 * at build time (server/src/missions.ts), so the only cycle that could reach
 * this is one no test caught, and the difference between a wrong back-link
 * and a hung tab is worth one `Set`.
 */
function prevRead(missions: PublicMission[], mission: PublicMission, progress: Progress): PublicMission | null {
  const byId = new Map(missions.map((m) => [m.id, m]));
  const seen = new Set<string>([mission.id]);
  let layer = mission.requires;
  while (layer.length > 0) {
    const behind: string[] = [];
    for (const id of layer) {
      if (seen.has(id)) continue;
      seen.add(id);
      const m = byId.get(id);
      // A prerequisite the catalogue cannot resolve ends that branch of the
      // walk and no more: the other prerequisites are still real levels with
      // real articles, and one missing id should not cost the reader the
      // back-link to them.
      if (!m) continue;
      if (levelProgress(progress, m.id).completed) return m;
      behind.push(...m.requires);
    }
    layer = behind;
  }
  return null;
}

export default Debrief;
